import { html, nothing, unsafeCSS, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import "@/components/menu-item";
import "@/components/icon";
import "@/components/shortcut";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import EkoElement from "@/utilities/element";
import style from "./command-menu-item.css?inline";

const SHORTCUT_ACTIVE_CLASS = "eko-command-menu-item__shortcut--active";

@customElement("eko-command-menu-item")
export class CommandMenuItem extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private handleKeyDown = (e: KeyboardEvent) => {
    if (this.shortcutRef.value && e.altKey) {
      this.shortcutActive = true;
      this.shortcutRef.value.classList.add(SHORTCUT_ACTIVE_CLASS);
    }
  };

  private handleKeyUp = (e: KeyboardEvent) => {
    if (this.shortcutRef.value && !e.altKey) {
      this.shortcutActive = false;
      this.shortcutRef.value.classList.remove(SHORTCUT_ACTIVE_CLASS);
    }
  };

  private shortcutRef: Ref<HTMLElement> = createRef();

  @property({ reflect: true, type: Boolean }) active?: boolean;
  @property({ reflect: true }) group?: string;
  @property({ reflect: true }) href?: string;
  @property({ reflect: true }) icon?: string;
  @property({ reflect: true }) shortcut?: string;

  @property({ reflect: true, type: Boolean }) shortcutActive: boolean = false;
  @property({ reflect: true }) target?: "_self" | "_blank" | "_parent" | "_top";

  connectedCallback() {
    super.connectedCallback();
    this.tabIndex = 0;

    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
  }

  render() {
    return this.href ? this.renderLink() : this.renderButton();
  }

  renderButton() {
    return html`
      <button class="eko-command-menu-item" type="button" tabindex="-1" part="item">
        ${this.renderContent()}
      </button>
    `;
  }

  renderContent() {
    return html`
      ${this.icon
        ? html`<eko-icon
            name=${this.icon}
            part="icon"
            class="eko-command-menu-item__icon"></eko-icon>`
        : nothing}
      <slot></slot>
      <div class="eko-command-menu-item__after" part="after">
        ${this.shortcut
          ? html`<div
              part="shortcut"
              ${ref(this.shortcutRef)}
              class="eko-command-menu-item__shortcut">
              <eko-shortcut>${this.shortcut}</eko-shortcut>
            </div>`
          : nothing}
        ${this.group
          ? html`<span class="eko-command-menu-item__group" part="group">${this.group}</span>`
          : nothing}
      </div>
    `;
  }

  renderLink() {
    return html`
      <a
        class="eko-command-menu-item"
        href=${ifDefined(this.href)}
        target=${ifDefined(this.target)}
        part="item">
        ${this.renderContent()}
      </a>
    `;
  }

  updated(changedProperties: PropertyValues) {
    if (this.shortcutRef.value && changedProperties.has("shortcutActive")) {
      if (this.shortcutActive) {
        this.shortcutRef.value.classList.add(SHORTCUT_ACTIVE_CLASS);
      } else {
        this.shortcutRef.value.classList.remove(SHORTCUT_ACTIVE_CLASS);
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-command-menu-item": CommandMenuItem;
  }
}

export default CommandMenuItem;
